import { useState } from "react";
import { Alert, Button, DataTable, DetailList, Dialog, Field, Pagination } from "../../../ui";
import { Panel, RowAction } from "../../workspace/shared";
import { failure } from "../../workspace/behavior";
import type { Schemas } from "../../workspace/api.generated";
import { useRegisteredNotificationTemplate, useRegisteredNotificationTemplates } from "../queries";

type TemplateSummary = Schemas["RegisteredNotificationTemplateSummary"];
type State = "idle" | "loading" | "ready" | "error";
const serviceStatus: Record<string, string> = { REG: "등록됨", REQ: "검수 중", REJ: "반려됨", STP: "사용 중지", RDY: "발송 준비", ACT: "사용 가능", DMT: "휴면", BLK: "차단됨" };
const inspectionStatus: Record<string, string> = { REG: "등록됨", REQ: "검수 중", REJ: "반려됨", APR: "승인됨" };

export interface RegisteredTemplatesViewProps {
  readonly state: State;
  readonly data?: Schemas["RegisteredNotificationTemplatePage"];
  readonly error?: string;
  readonly selected?: TemplateSummary;
  readonly detailState: State;
  readonly detail?: Schemas["RegisteredNotificationTemplateDetail"];
  readonly detailError?: string;
  readonly onSearch: (keyword: string) => void;
  readonly onPageChange: (page: number) => void;
  readonly onSelect: (template: TemplateSummary) => void;
  readonly onCloseDetail: () => void;
  readonly onRetryDetail: () => void;
}

export function RegisteredTemplatesView(props: RegisteredTemplatesViewProps) {
  const [keyword, setKeyword] = useState("");
  const [searchError, setSearchError] = useState("");
  return <>
    <Panel title="비즈뿌리오 등록 템플릿" description="활성 비즈뿌리오 설정의 발신 프로필로 등록된 알림톡 템플릿을 조회합니다. 발송 모드와 관계없이 KAPI에 실제 조회를 요청합니다.">
      <form className="admin-filter-bar" onSubmit={(event) => {
        event.preventDefault();
        const search = keyword.trim();
        if (search && (search.length < 2 || search.length > 50)) { setSearchError("검색어는 2~50자로 입력해 주세요."); return; }
        setSearchError(""); props.onSearch(search);
      }}>
        <Field compact label="템플릿 검색" description="이름 또는 코드로 검색합니다. 비워두면 전체 목록을 조회합니다." maxLength={50} value={keyword} error={searchError} onChange={(event) => { setKeyword(event.currentTarget.value); setSearchError(""); }} />
        <Button size="small" type="submit" isLoading={props.state === "loading"} loadingLabel="템플릿 조회 중">등록 템플릿 조회</Button>
      </form>
      {props.state === "idle" ? <Alert persistent variant="information" title="등록된 템플릿을 조회해 주세요">연동 설정에 KAPI 계정 ID·인증키와 발신 프로필 키를 등록한 뒤 조회할 수 있습니다.</Alert> : null}
      {props.state === "error" ? <Alert persistent variant="danger" title="등록 템플릿을 불러오지 못했습니다" action={<a className="admin-row-link" href="/integrations">연동 설정 확인</a>}>{props.error || "연동 설정과 KAPI 조회 권한을 확인한 뒤 다시 조회해 주세요."}</Alert> : null}
      {props.state === "ready" || props.state === "loading" ? <DataTable caption="비즈뿌리오 등록 템플릿" state={props.state} rows={props.state === "ready" ? props.data?.items ?? [] : []} rowKey={(row) => `${row.senderKeyType}-${row.templateCode}`} columns={[
        { key: "name", title: "템플릿 이름", render: (row: TemplateSummary) => row.templateName },
        { key: "code", title: "템플릿 코드", render: (row: TemplateSummary) => row.templateCode },
        { key: "status", title: "상태", render: (row: TemplateSummary) => serviceStatus[row.serviceStatus] ?? "상태 확인 필요" },
        { key: "profile", title: "발신 프로필", render: (row: TemplateSummary) => row.senderKeyType === "G" ? "그룹" : "일반" },
        { key: "action", title: "내용", render: (row: TemplateSummary) => <RowAction onClick={() => props.onSelect(row)} ariaLabel={`${row.templateName} 내용 보기`}>내용 보기</RowAction> },
      ]} /> : null}
      {props.state === "ready" && props.data ? <Pagination page={Math.max(0, props.data.page - 1)} size={props.data.size} totalCount={props.data.totalCount} onPageChange={(page) => props.onPageChange(page + 1)} /> : null}
    </Panel>
    <Dialog open={Boolean(props.selected)} size="medium" title={props.selected?.templateName ?? "등록 템플릿 내용"} description={props.selected?.templateCode} onClose={props.onCloseDetail} secondaryAction={<Button variant="quiet" onClick={props.onCloseDetail}>닫기</Button>}>
      {props.detailState === "loading" ? <p role="status">템플릿 본문을 불러오는 중입니다.</p> : null}
      {props.detailState === "error" ? <Alert persistent variant="danger" title="템플릿 내용을 불러오지 못했습니다" action={<Button variant="quiet" onClick={props.onRetryDetail}>다시 조회</Button>}>{props.detailError || "다시 조회해 주세요."}</Alert> : null}
      {props.detailState === "ready" && props.detail ? <>
        <DetailList items={[
          { label: "검수 결과", value: inspectionStatus[props.detail.inspectionStatus] ?? "상태 확인 필요" },
          { label: "사용 상태", value: props.detail.blocked ? "차단됨" : props.detail.dormant ? "휴면" : props.detail.status === "A" ? "정상" : props.detail.status === "S" ? "사용 중지" : "발송 대기" },
        ]} />
        {props.detail.title ? <Field label="강조 제목" value={props.detail.title} readOnly /> : null}
        <Field control="textarea" label="등록된 본문" value={props.detail.content} readOnly rows={8} description="비즈뿌리오에 등록된 원문입니다. 변수 표기는 등록된 그대로 표시합니다." />
        {props.detail.extra ? <Field control="textarea" label="부가 안내" value={props.detail.extra} readOnly rows={3} /> : null}
        {props.detail.buttons.length ? <DataTable caption="등록된 버튼" rows={props.detail.buttons} rowKey={(row) => `${row.name}-${row.linkType}`} columns={[
          { key: "name", title: "버튼 이름", render: (row: Schemas["RegisteredNotificationTemplateButton"]) => row.name },
          { key: "mobile", title: "모바일 연결 주소", render: (row: Schemas["RegisteredNotificationTemplateButton"]) => row.mobileUrl ?? "—" },
          { key: "pc", title: "PC 연결 주소", render: (row: Schemas["RegisteredNotificationTemplateButton"]) => row.pcUrl ?? "—" },
        ]} /> : null}
      </> : null}
    </Dialog>
  </>;
}

export function RegisteredTemplates() {
  const [search, setSearch] = useState<{ page: number; keyword: string }>();
  const [selected, setSelected] = useState<TemplateSummary>();
  const list = useRegisteredNotificationTemplates(search?.page ?? 1, search?.keyword ?? "", Boolean(search));
  const detail = useRegisteredNotificationTemplate(selected?.templateCode, selected?.senderKeyType);
  return <RegisteredTemplatesView
    state={!search ? "idle" : list.isError ? "error" : list.isFetching || list.isPending ? "loading" : "ready"}
    data={list.data}
    error={list.error ? failure(list.error).message : undefined}
    selected={selected}
    detailState={!selected ? "idle" : detail.isError ? "error" : detail.isFetching || detail.isPending ? "loading" : "ready"}
    detail={detail.data}
    detailError={detail.error ? failure(detail.error).message : undefined}
    onSearch={(keyword) => { setSelected(undefined); if (search?.page === 1 && search.keyword === keyword) void list.refetch(); else setSearch({ page: 1, keyword }); }}
    onPageChange={(page) => { setSelected(undefined); setSearch((current) => ({ page, keyword: current?.keyword ?? "" })); }}
    onSelect={setSelected}
    onCloseDetail={() => setSelected(undefined)}
    onRetryDetail={() => { void detail.refetch(); }}
  />;
}
